// @flow
import * as React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import keyBy from 'lodash/fp/keyBy';
import identity from 'lodash/identity';
import { useIntl, type MessageDescriptor } from 'react-intl';

import EntryTagPopover from '~components/EntryTagPopover';
import messages from './messages';
import useEntryTags from '~hooks/useEntryTags';
import type {
  PrismicNewsEntry,
  PrismicPressEntry,
  PrismicUseCase,
} from '~schema';

import useFiltersState from '~components/FiltersProvider/hooks/useFiltersState';
import useFiltersActions from '~components/FiltersProvider/hooks/useFiltersActions';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(3),
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
  },
  chips: {
    [theme.breakpoints.down('xs')]: {
      textAlign: 'center',
    },
    padding: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(1),
  },
  popoverButton: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

export default function useArchiveFilters(
  entries: ?Array<?PrismicNewsEntry | ?PrismicPressEntry | ?PrismicUseCase>,
) {
  const selectedTagsIds = useFiltersState().entryTags;
  const selectedTagsIdsDict = React.useMemo(
    () => keyBy(identity)(selectedTagsIds),
    [selectedTagsIds],
  );
  const setSelectedTags = useFiltersActions().setEntryTags;

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const tags = useEntryTags();
  const intl = useIntl();

  const takeMessage = React.useCallback(
    (message: ?MessageDescriptor | ?string): ?string =>
      typeof message === 'string'
        ? message
        : (message && intl.formatMessage(message)) || null,
    [intl.locale],
  );

  const selectedTags = React.useMemo(() => {
    return tags
      ? tags.filter(tag => !!tag && !!selectedTagsIdsDict[tag.uid])
      : [];
  }, [tags, selectedTagsIds]);

  const filteredEntries = React.useMemo<typeof entries>(
    () =>
      selectedTagsIds.length && entries
        ? entries.filter(
            entry =>
              entry &&
              entry.data &&
              entry.data.tags.some(({ tag }) =>
                selectedTags.some(
                  selectedTag =>
                    selectedTag &&
                    tag.document &&
                    selectedTag.uid === tag.document.uid,
                ),
              ),
          )
        : entries,
    [selectedTagsIds, entries],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOpen = React.useCallback(
    event => setAnchorEl(event.currentTarget),
    [setAnchorEl],
  );

  const handleUnselect = React.useCallback(
    tag =>
      setSelectedTags(
        tag
          ? selectedTagsIds.filter(tagId => tagId !== tag.uid)
          : selectedTagsIds,
      ),
    [selectedTagsIds],
  );

  const isTagSelected = React.useCallback(
    tag => !!tag && !!selectedTagsIdsDict[tag.uid],
    [selectedTagsIdsDict],
  );

  return [
    <div className={classes.root} key="filter">
      <div className={classes.chips}>
        {selectedTags.map(tag => (
          <Chip
            key={tag?.data?.name?.text}
            label={tag?.data?.name?.text}
            onDelete={
              isTagSelected(tag) ? () => handleUnselect(tag) : undefined
            }
            color={isTagSelected(tag) ? 'primary' : 'secondary'}
            className={classes.chip}
          />
        ))}
      </div>

      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        className={classes.popoverButton}
      >
        {takeMessage(messages.filterButton)}
      </Button>

      <EntryTagPopover
        tags={tags}
        anchorEl={anchorEl}
        onClose={handleClose}
        onTagClick={React.useCallback(
          (unusedEvent, tag) => {
            setSelectedTags(
              isTagSelected(tag)
                ? selectedTagsIds.filter(selectedTag => selectedTag !== tag.uid)
                : [...selectedTagsIds, tag.uid],
            );
          },
          [selectedTagsIds, setSelectedTags],
        )}
        onUnselectAll={React.useCallback(() => setSelectedTags([]), [
          setSelectedTags,
        ])}
        isTagSelected={isTagSelected}
      />
    </div>,
    filteredEntries,
  ];
}
